import { createGamePlayerReference } from '../../../../firebase/firebaseAPICalls/FirebaseAPICallsGamePlayer';
import { SportsType } from '../../../enums/SportsType';
import { TagType } from '../../../enums/TagType';
import { isValidString } from '../../../Validity';
import { BaseballGameObject, BaseballGamePromotionalVideoObject } from './BaseballGameInterface';

export interface BaseballGamePlayerObject {
    id: string,
    reference: any,
    teamId: string,
    teamReference: any,
    organizationId: string,
    organizationReference: any,
    gameId: string,
    gameReference: any,
    seasonId: string,
    seasonReference: any,
    preDevelopedSeasonId: string,
    preDevelopedSeasonReference: any,
    leagueId: string,
    leagueReference: any,
    preDevelopedLeagueId: string,
    preDevelopedLeagueReference: any,
    preDevelopedConferenceId: string,
    preDevelopedConferenceReference: any,
    opponentId: string,
    opponentReference: any,
    winOrLoss: 'win' | 'loss' | 'tied',
    gameScore: string,
    shutOut: boolean,
    gamesPlayed: number,
    AB: number,
    R: number,
    H: number,
    '2B': number,
    '3B': number,
    HR: number,
    RBI: number,
    BB: number,
    IBB: number,
    SO: number,
    HBP: number,
    SH: number,
    SF: number,
    SB: number,
    CS: number,
    LOB: number,
    PO: number,
    A: number,
    E: number,
    DP: number,
    title: string,
    sportsType: SportsType,
    homeGame: boolean,
    status: 'scheduled' | 'postponed' | 'finished' | 'tba' | 'tbat' | 'cancelled',
    timeOfGame: Date,
    venue: string,
    attendance: number,
    featureImage: string,
    promotionalText: string,
    promotionalVideo: BaseballGamePromotionalVideoObject,
    notes: string,
    timeModified: Date,
    timeCreated: Date,
}

export const getPlayerIdsFromBaseballGameObject = (gameObject: BaseballGameObject): string[] => {
    const playerIds: Set<string> = new Set();
    gameObject.tagIds.forEach((tagId, index) => {
        if (gameObject.tagTypes[index] === TagType.PLAYER) {
            playerIds.add(tagId);
        }
    });
    if (gameObject.playerStats) {
        gameObject.playerStats.forEach((player) => {
            if (isValidString(player.id)) {
                playerIds.add(player.id);
            }
        });
    }
    return Array.from(playerIds);
};

export const getBaseballGamePlayerObjectFromBaseballGameObject = (gameObject: BaseballGameObject, playerId: string): BaseballGamePlayerObject => {
    const playerReference = createGamePlayerReference(gameObject.id, playerId);

    let AB = 0;
    let R = 0;
    let H = 0;
    let doubles = 0;
    let triples = 0;
    let HR = 0;
    let RBI = 0;
    let BB = 0;
    let IBB = 0;
    let SO = 0;
    let HBP = 0;
    let SH = 0;
    let SF = 0;
    let SB = 0;
    let CS = 0;
    let LOB = 0;
    let PO = 0;
    let A = 0;
    let E = 0;
    const DP = 0;

    if (gameObject.playerStats) {
        gameObject.playerStats.forEach((player) => {
            if (player.id === playerId) {
                AB += player.AB || 0;
                R += player.R || 0;
                H += player.H || 0;
                doubles += player['2B'] || 0;
                triples += player['3B'] || 0;
                HR += player.HR || 0;
                RBI += player.RBI || 0;
                BB += player.BB || 0;
                IBB += player.IBB || 0;
                SO += player.SO || 0;
                HBP += player.HBP || 0;
                SH += player.SH || 0;
                SF += player.SF || 0;
                SB += player.SB || 0;
                CS += player.CS || 0;
                LOB += player.LOB || 0;
                PO += player.PO || 0;
                A += player.A || 0;
                E += player.E || 0;
            }
        });
    }

    return {
        id: playerId,
        reference: playerReference,
        teamId: gameObject.teamId,
        teamReference: gameObject.teamReference,
        organizationId: gameObject.organizationId,
        organizationReference: gameObject.organizationReference,
        gameId: gameObject.id,
        gameReference: gameObject.reference,
        seasonId: gameObject.seasonId,
        seasonReference: gameObject.seasonReference,
        preDevelopedSeasonId: gameObject.preDevelopedSeasonId,
        preDevelopedSeasonReference: gameObject.preDevelopedSeasonReference,
        leagueId: gameObject.leagueId,
        leagueReference: gameObject.leagueReference,
        preDevelopedLeagueId: gameObject.preDevelopedLeagueId,
        preDevelopedLeagueReference: gameObject.preDevelopedLeagueReference,
        preDevelopedConferenceId: gameObject.preDevelopedConferenceId,
        preDevelopedConferenceReference: gameObject.preDevelopedConferenceReference,
        opponentId: gameObject.opponentId,
        opponentReference: gameObject.opponentReference,
        winOrLoss: gameObject.winOrLoss,
        gameScore: gameObject.gameScore,
        shutOut: gameObject.shutOut,
        gamesPlayed: 1,
        AB,
        R,
        H,
        '2B': doubles,
        '3B': triples,
        HR,
        RBI,
        BB,
        IBB,
        SO,
        HBP,
        SH,
        SF,
        SB,
        CS,
        LOB,
        PO,
        A,
        E,
        DP,
        title: gameObject.title,
        sportsType: gameObject.sportsType,
        homeGame: gameObject.homeGame,
        status: gameObject.status,
        timeOfGame: gameObject.timeOfGame,
        venue: gameObject.venue,
        attendance: gameObject.attendance,
        featureImage: gameObject.featureImage,
        promotionalText: gameObject.promotionalText,
        promotionalVideo: gameObject.promotionalVideo,
        notes: gameObject.notes,
        timeModified: gameObject.timeModified,
        timeCreated: gameObject.timeCreated,
    };
};
